import { api } from 'API';
import {
  EXISTING_FORM_IMPROVEMENT_AREAS,
  FEEDBACK_STATUSES,
  HELPFULNESS_RATING,
  NEGATIVE_FEEDBACK_REASONS,
  NEW_FORM_IMPROVEMENT_AREAS,
} from 'Enum/Feedback';
import { addImposterToUrl } from 'Utils';
import qs from 'qs';

const baseUrl = '/feedback';

export interface IPageMenuItem {
  id: number;
  description: string;
}

export interface IFeedbackSearchParams {
  PageSize?: number;
  PageNumber?: number;
  Id?: number;
  UtcFromDate?: string;
  UtcToDate?: string;
  ContainingPageNumbers?: number[];
  ImprovementAreas?: number[];
  NegativeFeedbackReasons?: number[];
  HelpfulnessRatings?: number[];
  Order?: number;
  StatusTypes?: number[];
}

export interface IFeedback {
  id: number;
  utcCreateDate: string;
  pageName: string;
  improvementArea: number;
  negativeFeedbackReason?: number;
  helpfulnessRating?: number;
  detailedPositiveFeedback: string;
  detailedImprovement: string;
  detailedTechnicalProblem: string;
  attachmentPath?: string;
  recordingAttachmentPath?: string;
  isrId?: string;
  fullName?: string;
  pageNumber: string;
  isRead?: boolean;
  feedbackStatusId: number;
  managerName?: string;
  deviceId?: number;
  browserId?: number;
  deviceDetails?: string;
  phoneNumber?: string;
  emailAddress?: string;
}

export interface IFeedbackSearchResult {
  feedbacks: IFeedback[];
  totalCount: number;
}

export interface IFeedbackMenuItem {
  id: number;
  description: string;
  isNew?: boolean;
  internalLink?: string;
}

export const getFeedback = async (params: IFeedbackSearchParams) => {
  try {
    const formData = qs.stringify(params);

    const response = await api.post<IFeedbackSearchResult>(
      `${baseUrl}`,
      formData,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching feedback:', error);
    throw error;
  }
};

export const getFeedbacksCsv = async (params: IFeedbackSearchParams) => {
  if (params.PageNumber) {
    params.PageNumber = undefined;
  }
  if (params.PageSize) {
    params.PageSize = undefined;
  }

  const formData = qs.stringify(params);

  const response = await api.post<Blob>(
    `${baseUrl}/GetFeedbacksCsv`,
    formData,
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      responseType: 'blob',
    }
  );

  if (response?.data) {
    return response.data;
  }
};

export const markFeedbackAsRead = async (feedbackId: number) => {
  const url = addImposterToUrl(`${baseUrl}/${feedbackId}/markAsRead`, true);
  const response = await api.post(url);
  if (response?.data) {
    return response.data;
  }
  return response.data;
};

export const getFeedbackMenuItems = async () => {
  const url = addImposterToUrl(`${baseUrl}/menuItems`, true);

  const response = await api.get<IFeedbackMenuItem[]>(url);
  return response.data;
};

export const getFeedbackFile = async (
  feedbackId: number,
  attachmentType: number
) => {
  const url = addImposterToUrl(
    `${baseUrl}/${feedbackId}/file?attachmentType=${attachmentType}`,
    true
  );
  const response = await api.get(url);
  return response.data;
};

export interface IFeedbackNewFormDTO {
  pageNumber: number;
  helpfulnessRating?: HELPFULNESS_RATING;
  newFormImprovementAreas?: NEW_FORM_IMPROVEMENT_AREAS;
  negativeFeedbackReason?: NEGATIVE_FEEDBACK_REASONS;
  detailedPositiveFeedback: string;
  detailedImprovement: string;
  detailedTechnicalProblem: string;
  attachmentData?: File;
  recordingAttachmentData?: File;
}

export interface IFeedbackExistingFormDTO {
  pageNumber: number;
  existingFormImprovementAreas?: EXISTING_FORM_IMPROVEMENT_AREAS;
  negativeFeedbackReason?: NEGATIVE_FEEDBACK_REASONS;
  detailedPositiveFeedback: string;
  detailedImprovement: string;
  detailedTechnicalProblem: string;
  attachmentData?: File;
  recordingAttachmentData?: File;
}

export const postFeedback = async (body: FormData, isNew?: boolean) => {
  api.post(
    addImposterToUrl(
      `${baseUrl}/${
        isNew ? 'SubmitNewFormFeedback' : 'SubmitExistingFormFeedback'
      }`,
      true
    ),
    body
  );
};

export interface ISitePagesDTO {
  id: number;
  description: string;
  isNew: boolean;
  internalLink: string;
}

export const getPageInfo = async (relativePath: string) => {
  const res = await api.get<ISitePagesDTO>(
    `${baseUrl}/menuItem?relativePath=${relativePath}`
  );
  if (res?.data) return res.data;
};

export const putFeedbackStatus = async (
  status: FEEDBACK_STATUSES,
  feedbackId: number
) => {
  const res = await api.put(
    `${baseUrl}/${feedbackId}/updateStatus?status=${status}`
  );
  if (res?.data) return res.data;
};
